import { useCallback, useSyncExternalStore } from 'react'
import { useAuth } from '@/hooks/useAuth'

const STORAGE_KEY = 'admin_view_mode'

function getSnapshot(): boolean {
  return localStorage.getItem(STORAGE_KEY) !== 'mine'
}

function subscribe(callback: () => void): () => void {
  window.addEventListener('storage', callback)
  return () => window.removeEventListener('storage', callback)
}

export function useViewMode() {
  const { user } = useAuth()
  const isAdmin = user?.role === 'admin'

  const viewAll = useSyncExternalStore(subscribe, getSnapshot)

  const toggleViewMode = useCallback(() => {
    if (!isAdmin) return
    const current = localStorage.getItem(STORAGE_KEY)
    localStorage.setItem(STORAGE_KEY, current === 'mine' ? 'all' : 'mine')
    window.dispatchEvent(new StorageEvent('storage', { key: STORAGE_KEY }))
  }, [isAdmin])

  if (!isAdmin) {
    return { viewAll: false, isAdmin: false, toggleViewMode: () => {} }
  }

  return { viewAll, isAdmin: true, toggleViewMode }
}

/**
 * View-mode value for API query params: `undefined` requests all data,
 * `'mine'` scopes results to the current admin. Centralizes the mapping
 * used by every data page so the param convention lives in one place.
 */
export function useViewModeParam(): 'mine' | undefined {
  const { viewAll } = useViewMode()
  return viewAll ? undefined : 'mine'
}
